/**
 * Runtime Validators using Valibot
 * Ensures API responses conform to expected shapes before use in components
 * 
 * All data from API must be parsed through these validators before trusting it.
 */

import {
  parse,
  object,
  string,
  number,
  boolean,
  array,
  optional,
  nullable,
  union,
} from 'valibot'

/**
 * Tariff Validator
 */
export const TariffSchema = object({
  id: string(),
  package_id: optional(string()),
  duration_months: optional(number()),
  price: optional(number()),
  old_price: optional(nullable(number())),
  discount_percent: optional(number()),
  is_popular: optional(boolean()),
  is_active: optional(boolean()),
  sort_order: optional(number()),
  device_markups: optional(array(number())),
})

/**
 * Package Validator
 */
export const PackageSchema = object({
  id: string(),
  name: string(),
  description: optional(nullable(string())),
  icon: optional(nullable(string())),
  is_popular: optional(boolean()),
  tariffs: array(TariffSchema),
})

/**
 * Config Validator
 */
export const ConfigSchema = object({
  id: string(),
  name: string(),
  price: number(),
  duration: string(),
  speed_limit: optional(nullable(string())),
  description: optional(nullable(string())),
})

/**
 * TimeOption Validator
 */
export const TimeOptionSchema = object({
  months: number(),
  price: number(),
})

/**
 * Subscription Validator
 */
export const SubscriptionSchema = object({
  id: string(),
  config_key: string(),
  package_name: string(),
  device_count: number(),
  expires_at: string(),
  is_active: boolean(),
  is_trial: optional(boolean()),
  access_key: optional(nullable(string())),
  country: optional(nullable(string())),
  device_markups: optional(array(number())),
  time_options: optional(array(TimeOptionSchema)),
})

/**
 * Payment Validator
 */
export const PaymentSchema = object({
  id: string(),
  config_id: optional(nullable(string())),
  amount: number(),
  status: string(),
  created_at: string(),
  completed_at: optional(nullable(string())),
})

/**
 * Transaction Validator
 */
export const TransactionSchema = object({
  id: string(),
  user_id: string(),
  type: string(),
  amount: number(),
  balance_before: number(),
  balance_after: number(),
  description: optional(nullable(string())),
  reference_id: optional(nullable(string())),
  created_at: string(),
  metadata: optional(object({})),
})

/**
 * User Validator
 */
export const UserSchema = object({
  id: optional(union([string(), number()])),
  telegram_id: optional(nullable(string())),
  telegramId: optional(nullable(string())),
  first_name: optional(nullable(string())),
  last_name: optional(nullable(string())),
  username: optional(nullable(string())),
  photo_url: optional(nullable(string())),
  referral_code: optional(nullable(string())),
  email: optional(nullable(string())),
  subscription_status: optional(nullable(string())),
  is_active: optional(boolean()),
  created_at: optional(nullable(string())),
  balance: optional(nullable(number())),
})

/**
 * Safe Parse Functions
 * These wrap Valibot's parse() with error handling
 */

type SafeParseResult<T> = {
  success: boolean
  data?: T
  error?: Error | string
}

/**
 * Safe parse with detailed error handling
 */
export const safeParse = <T,>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any,
  data: unknown
): SafeParseResult<T> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = parse(schema, data)
    return { success: true, data: result as T }
  } catch (error) {
    const errorMessage = error instanceof Error
      ? error.message
      : String(error)
    return { success: false, error: errorMessage }
  }
}

/**
 * Array parse helper - filters out invalid items
 */
export const safeParseArray = <T,>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any,
  items: unknown[]
): T[] => {
  if (!Array.isArray(items)) return []
  
  return items
    .map((item: unknown) => {
      const result = safeParse<T>(schema, item)
      return result.success ? result.data : null
    })
    .filter((item): item is T => item !== null)
}

/**
 * Export validators for use in adapters
 */
export const validators = {
  tariff: (data: unknown) => safeParse(TariffSchema, data),
  package: (data: unknown) => safeParse(PackageSchema, data),
  config: (data: unknown) => safeParse(ConfigSchema, data),
  subscription: (data: unknown) => safeParse(SubscriptionSchema, data),
  payment: (data: unknown) => safeParse(PaymentSchema, data),
  transaction: (data: unknown) => safeParse(TransactionSchema, data),
  user: (data: unknown) => safeParse(UserSchema, data),
  tariffArray: (data: unknown) => safeParseArray(TariffSchema, Array.isArray(data) ? data : []),
  packageArray: (data: unknown) => safeParseArray(PackageSchema, Array.isArray(data) ? data : []),
  subscriptionArray: (data: unknown) => safeParseArray(SubscriptionSchema, Array.isArray(data) ? data : []),
  transactionArray: (data: unknown) => safeParseArray(TransactionSchema, Array.isArray(data) ? data : []),
}
